import endpoints from "../config/endpoints.json";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import axios from "axios";
import type { ApiResponse } from "../types";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

/**
 * Category
 */
export const queryCategoryListByVersionCodeList = async (
  queryCategoryParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories.context}${endpoints.categories.query["by-versionCodeList"].context}`,
      queryCategoryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Subject Description
 */
export const retrieveSubjectDescriptionDTOWithData = async (
  id,
): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.category.url}${endpoints.categories["subject-descriptions"].context}${endpoints.categories["subject-descriptions"].dto["with-data"].id.context}`.replace(
        "{id}",
        id,
      ),
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const querySubjectDescriptionListByCategoryIdList = async (
  queryParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories["subject-descriptions"].context}${endpoints.categories["subject-descriptions"].query.dto["by-categoryIdList"].context}`,
      queryParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Subject Description Completion
 */
export const createSubjectDescriptionCompletion = async (
  createParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories["subject-description-completions"].context}/`,
      createParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const querySubjectDescriptionCompletionListByUserIdAndUserProfileId =
  async (queryParams): Promise<ApiResponse> => {
    try {
      const response = await axios.post(
        `${connection.base.category.url}${endpoints.categories["subject-description-completions"].context}${endpoints.categories["subject-description-completions"].query.dto["by-userId-userProfileId"].context}`,
        queryParams,
      );
      return { data: response.data, status: response.status };
    } catch (err) {
      return { err };
    }
  };

export const patchSubjectDescriptionCompletion = async (
  patchParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.patch(
      `${connection.base.category.url}${endpoints.categories["subject-description-completions"].context}/`,
      patchParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Subject Description Chat
 */
export const initializeSubjectDescriptionChatWithText = async (
  startParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories["subject-description-chats"].context}${endpoints.categories["subject-description-chats"].initialize.context}`,
      startParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const addSubjectDescriptionChatWithText = async (
  subjectDescriptionChatId,
  addParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories["subject-description-chats"].context}${endpoints.categories["subject-description-chats"]["subject-description-chat-id"].items.context}`.replace(
        "{subjectDescriptionChatId}",
        subjectDescriptionChatId,
      ),
      addParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Skill
 */
export const querySkillListByVersionCodeList = async (
  querySkillParams,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories.skills.context}${endpoints.categories.skills.query["by-versionCodeList"].context}`,
      querySkillParams,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Vocabulary cards
 */

/**
 * Lists the vocabulary a learner is studying at this outcome, as flash cards.
 * Read-only; served from committed backend JSON (no DB).
 *
 * The curriculum states its word list ONCE per theme, on a single outcome, so
 * the backend falls back to the theme when the outcome asked for has no set of
 * its own: a learner working on the theme's grammar or reading outcome gets the
 * same deck as one working on its vocabulary outcome. Callers therefore pass
 * whatever outcome the learner is on and do not resolve the theme themselves.
 * { categoryId }.
 */
export const queryVocabularyCardListByCategoryId = async (
  params,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories["vocabulary-cards"].context}${endpoints.categories["vocabulary-cards"].query["by-categoryId"].context}`,
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Asks, in ONE call, which of several outcomes can offer a vocabulary deck.
 * The learning path renders many outcome cards at once and each needs the
 * answer before the learner touches anything — a deck button that opens an
 * empty deck is worse than no button — so this is batched rather than one
 * request per card. Returns a row per id with its `cardCount` (0 = no deck).
 * { categoryIdList }.
 */

export const queryVocabularyCardAvailabilityByCategoryIdList = async (
  params,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories["vocabulary-cards"].context}${endpoints.categories["vocabulary-cards"].query.availability["by-categoryIdList"].context}`,
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Term cards
 *
 * The non-language counterpart of the vocabulary deck: a theme's terms with the
 * explanation that defines each, in the language of instruction. A term card is
 * MONOLINGUAL — there is no translation face, no part of speech and no example
 * sentence — so it is its own shape rather than a vocabulary card with eight
 * permanently empty fields.
 */

/**
 * The terms a theme teaches, as study cards. Read-only; served from committed
 * backend JSON (no DB).
 *
 * A subject curriculum states its terms against the THEME as a whole rather
 * than against a chosen outcome, so callers pass the theme's own category id —
 * which is what the learning path's section divider holds. An outcome's id
 * works too: the backend walks up to the theme.
 * { categoryId }.
 */
export const queryTermCardListByCategoryId = async (
  params,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories["term-cards"].context}${endpoints.categories["term-cards"].query["by-categoryId"].context}`,
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Asks, in ONE call, which of several themes can offer a term deck. The path
 * draws every theme of a lesson at once and each divider needs the answer
 * before the learner touches anything — a deck button that opens an empty deck
 * is worse than no button — so this is batched rather than one request per
 * theme. Returns a row per id with its `cardCount` (0 = no deck).
 * { categoryIdList }.
 */
export const queryTermCardAvailabilityByCategoryIdList = async (
  params,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.category.url}${endpoints.categories["term-cards"].context}${endpoints.categories["term-cards"].query.availability["by-categoryIdList"].context}`,
      params,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};
